import { useEffect } from "react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { INDUSTRIES } from "@/data/site";

const NOTES = [
  "POS terminals, digital signage, network infrastructure and cameras across sales floors and back-of-house.",
  "ATM and payment technology deployment, secure network infrastructure and branch technology support.",
  "Structured cabling, network buildouts, meeting-space displays and workplace technology installation.",
  "Guest-facing displays, property networks, POS systems and surveillance infrastructure.",
  "Network infrastructure, display technology and equipment installation in clinical and administrative environments.",
  "Rugged network infrastructure, scanning and device connectivity, cameras and facility technology.",
  "Building-wide cabling, connectivity, signage and security technology for managed properties.",
  "Standardized technology rollouts and repeatable installations across every site you operate.",
];

export default function Industries() {
  useEffect(() => {
    document.title = "Industries — LEDOS Enterprise Solutions";
  }, []);

  return (
    <main data-testid="industries-page">
      <section className="relative bg-ink pt-44 pb-20 md:pb-28 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal mb-8">
              Industries
            </p>
          </MaskedLine>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
            <MaskedLine delay={0.25}>Wherever technology</MaskedLine>
            <MaskedLine delay={0.38}>
              <span>
                has to <span className="text-signal">physically work.</span>
              </span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5} className="mt-10 max-w-2xl">
            <p className="text-base md:text-lg leading-relaxed text-zinc-400">
              LEDOS capabilities are relevant to any environment where business depends on
              physical technology infrastructure. These are the types of environments our
              services are built for.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="border-t border-line">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 0.03}>
                <div
                  data-testid={`industry-row-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group grid md:grid-cols-12 gap-6 items-center py-8 md:py-10 border-b border-line px-2 md:px-6 hover:bg-panel transition-colors duration-500"
                >
                  <span className="md:col-span-1 font-display text-3xl font-black tracking-tighter text-outline-signal">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="md:col-span-3 relative aspect-[16/9] overflow-hidden">
                    <img
                      src={ind.image}
                      alt={ind.name}
                      className="absolute inset-0 w-full h-full object-cover img-field opacity-70"
                    />
                  </div>
                  <h2 className="md:col-span-3 font-display text-2xl md:text-3xl font-bold tracking-tight text-white group-hover:text-signal transition-colors duration-500">
                    {ind.name}
                  </h2>
                  <p className="md:col-span-5 text-sm leading-relaxed text-zinc-500">
                    {NOTES[i]}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-16">
            <div className="border border-line bg-panel p-8 md:p-12">
              <SectionHeading
                index="N."
                eyebrow="Your Environment"
                title="Do Not See Your Industry Listed?"
                className="mb-6"
              />
              <p className="text-base leading-relaxed text-zinc-400 max-w-2xl">
                If your business runs on physical technology — networks, cabling, payment
                systems, cameras or displays — the work is likely familiar to us. Tell us what
                you need deployed and where, and we will tell you honestly whether we are the
                right fit.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
